// Port of companion/src/script/parse.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250), extended
// by Round 1, lane A (docs/briefs/ROUND1-SPEC-2026-09-05.md sections B, C, D, E). Everything the
// spec does not change reads exactly as the frozen parser did: the same blocks in the same order with
// the same keys, the same markers with the same reasons and key order, the same targets resolved.
// Three spellings differ for the editor-client lint profile and nothing else: the frozen loop's
// `continue` is a `return` from readLine (each branch returns the index of the next unread line),
// `for...of` is an index loop or forEach, and `??=` is written out.
//
// Round 1 adds, each marked `// Round 1` at the spot: the backslash escape (B.1: an escaped line is
// text, `\` alone is an empty line inside a paragraph, inline escapes are unmasked); an answer line
// is `*` followed by a space or the end of the line (B.2); the Unicode NAME pattern with the
// ` [texting]` / ` [thinking]` / ` [reaction]` tags (B.3, C); the choice prompt's speaker (D.1);
// `> end: <text>` carrying its text (C); `> results:`, `> player select`, `> achieve:`, `> video:`,
// `> roll:` and the `> check:` envelope (C, E.4); the engine's check operators, the two-word
// spellings and the `unknown check operator` marker (E.1, E.2); an unrouted machine arm (G.2);
// `> remember: x += v` / `-= v` and `> view: x in places` (E.5).
import type {
  AchieveBlock,
  Block,
  CharacterBlock,
  CharacterMode,
  CheckArm,
  CheckEnvelope,
  ChoiceAnswer,
  ChoiceBlock,
  EngineCheckOperator,
  EngineId,
  HubBlock,
  IfDirective,
  LegacyBlock,
  Marker,
  NameLookup,
  ParseOptions,
  RememberVerb,
  RollBlock,
  RoutedItem,
  Scene,
  ScriptAst,
  ScriptRoll,
  ScriptValue,
  SetDirective,
  VideoBlock,
} from '../../../dorian-shared/types/episodeScript';
import {
  CHARACTER_TAG,
  NAME_PATTERN,
  isEmptyTextLine,
  isEscapedLine,
  maskEscapes,
  unescapeLine,
} from './escape';

// The frozen arrow, and its global twin for finding every unescaped arrow in a line.
const ARROW_PATTERN = /\s+(?:→|->)\s+/;
const ARROWS = /\s+(?:→|->)\s+/g;

// U+FEFF. Spelled by code point rather than written out, because the character
// itself is invisible in a source file and an editor may quietly drop it.
const BYTE_ORDER_MARK = String.fromCharCode(0xFEFF);

// Round 1 (E.1): the operators a check may be written with, one to one with the engine's scalar six
// (utils/const.js:12). The frozen `greaterorequal`, `lessorequal` and `contains` are gone: the engine
// refuses them (F-06). Anything else in that position is part of the variable name, so
// `> if: must not drive somnus fast` reads as one name and `> if: count greater 2` as a comparison —
// unless the line ends in a typed value, which is the one shape that names an operator the engine
// does not have (E.2, marker `unknown check operator`).
const OPERATORS: Readonly<Record<string, EngineCheckOperator>> = Object.freeze({
  equal: 'equal',
  '==': 'equal',
  '=': 'equal',
  is: 'equal',
  notequal: 'notEqual',
  not_equal: 'notEqual',
  '!=': 'notEqual',
  '<>': 'notEqual',
  greater: 'greater',
  '>': 'greater',
  atleast: 'atLeast',
  '>=': 'atLeast',
  less: 'less',
  '<': 'less',
  atmost: 'atMost',
  '<=': 'atMost',
});

// The two-word spellings, checked before the single words so `is not` is not read as `is`.
const PHRASE_OPERATORS: Readonly<Record<string, EngineCheckOperator>> = Object.freeze({
  'is not': 'notEqual',
  'at least': 'atLeast',
  'at most': 'atMost',
});

// The engine spells "the variable is true" as equal 1, so that is what the
// spec's bare `> if: name` and `> remember: name` compile to.
const TRUE_VALUE = 1;
const FALSE_VALUE = 0;

// Round 1 (E.5): where a `> view:` may show a variable; any other word is a `malformed value`.
const VIEW_PLACES = new Set(['hide', 'alert', 'panel', 'hud', 'summary']);

// Round 1 (C, type 6): the engine's result sub-types; any other word is a `malformed value`.
const RESULT_SUB_TYPES = new Set(['choices', 'charts', 'traits']);

/** resolveToken's answer: the id a token names, and whether it named one at all. */
interface Resolved {
  id: EngineId | null;
  resolved: boolean;
}

/** `== NAME @ location` taken apart. */
interface Heading {
  name: string;
  location: string | null;
}

/**
 * `> if: variable [operator value]` taken apart; `bare` when only the variable was written;
 * `unknownOperator` is the writer's operator when it is not one the engine has (E.2).
 */
interface CheckSplit {
  variable: string;
  operator: EngineCheckOperator | null;
  value: ScriptValue;
  bare: boolean;
  unknownOperator: string | null;
}

/** `NAME (mood) [tag]` taken apart (B.3, C.2). */
interface Person {
  name: string;
  expression: string | undefined;
  mode: CharacterMode | undefined;
}

/** What resolves a target by scene name: a `> go to:`, an answer, an arm or a hub card. */
interface Routable {
  target: string | null;
  line: number;
  targetScene?: string;
}

/** How the arms of an `if` were spelled: named values, or the lines that follow. */
type ArmStyle = 'human' | 'machine' | null;

function parseValue(source: string | null | undefined): ScriptValue {
  if (source == null || source.trim() === '') return null;
  const value = source.trim();
  try {
    return JSON.parse(value) as ScriptValue;
  } catch {
    return value;
  }
}

// Round 1 (E.2, E.3): a token that carries a JSON type — a number, a quoted string, true, false, an
// object or an array. A bare word is a string by the frozen rule and is not "typed".
function isTypedValue(token: string): boolean {
  const trimmed = token.trim();
  if (trimmed === '' || trimmed === 'null') return false;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed !== null && (typeof parsed !== 'string' || /^".*"$/.test(trimmed));
  } catch {
    return false;
  }
}

// Round 1 (B.1): the pieces of `source` between its unescaped matches of a global pattern. The
// pattern runs on the masked string; the pieces are cut from the original, escapes still in them.
function splitUnescaped(source: string, pattern: RegExp): string[] {
  const masked = maskEscapes(source);
  const pieces: string[] = [];
  let last = 0;
  const matcher = new RegExp(pattern.source, 'g');
  let match = matcher.exec(masked);
  while (match !== null) {
    pieces.push(source.slice(last, match.index));
    last = match.index + match[0].length;
    if (match[0].length === 0) matcher.lastIndex += 1;
    match = matcher.exec(masked);
  }
  pieces.push(source.slice(last));
  return pieces;
}

// Round 1 (B.1): a text line as the step reads it. A line that is exactly `\` is the empty line
// inside a paragraph the writer could not otherwise write; every other line is unescaped.
function textLine(line: string): string {
  return isEmptyTextLine(line) ? '' : unescapeLine(line);
}

// Round 1 (G.2): the leading whitespace is kept while the arrow is looked for, so an item whose text
// is empty still finds its route — the decompiler writes an empty answer with a route as `*  → SCENE`
// (frozen `priced('')` plus the frozen ` → `), and the frozen parser, which trimmed first, read the
// arrow and the scene as the answer's text. The trailing whitespace is dropped as the frozen did.
function parseRoutedItem(source: string): RoutedItem {
  const pieces = splitUnescaped(source.replace(/\s+$/, ''), ARROWS);
  const target = pieces.length > 1 ? unescapeLine(String(pieces.pop()).trim()) : null;
  const joined = pieces.join(' → ').trim();
  // The price bracket is found on the masked text and cut from the original at the same index.
  const priceMatch = maskEscapes(joined).match(/^(.*?)\s+\[(-?\d+(?:\.\d+)?)\]\s*$/);
  const text = unescapeLine(priceMatch ? joined.slice(0, priceMatch[1].length) : joined);
  const price = priceMatch ? Number(priceMatch[2]) : 0;
  return { text, price, target };
}

function resolveToken(token: string | null | undefined, domain: NameLookup | undefined, prefix: string): Resolved {
  if (!token) return { id: null, resolved: false };
  const synthetic = token.match(new RegExp(`^${prefix}(-?\\d+)$`, 'i'));
  if (synthetic) return { id: Number(synthetic[1]), resolved: true };
  const id = domain?.byName?.get(token.toLocaleLowerCase());
  if (id === undefined) return { id: null, resolved: false };
  return { id: /^-?\d+$/.test(String(id)) ? Number(id) : id, resolved: true };
}

function parseHeading(line: string): Heading {
  const body = line.replace(/^==\s*/, '').trim();
  // Round 1 (B.1): the split is the last UNESCAPED ` @ `.
  const at = maskEscapes(body).lastIndexOf(' @ ');
  if (at < 0) return { name: unescapeLine(body), location: null };
  return { name: unescapeLine(body.slice(0, at).trim()), location: unescapeLine(body.slice(at + 3).trim()) };
}

// Round 1 (B.1): every construct test is false on an escaped line — it is text.
function isScene(line: string): boolean {
  return !isEscapedLine(line) && /^==(?:\s|$)/.test(line);
}

// Round 1 (B.2, F-02): an answer line is `*` followed by a space or the end of the line, so
// `*Bold* opening` is prose.
function isChoice(line: string): boolean {
  return !isEscapedLine(line) && /^\s*\*(?:\s|$)/.test(line);
}

function isDirective(line: string): boolean {
  return !isEscapedLine(line) && /^\s*>\s?/.test(line);
}

function isHubOption(line: string): boolean {
  return !isEscapedLine(line) && /^\s*-\s?/.test(line);
}

// Principle 3: anything Basil cannot infer is marked with "?" in the margin.
// A margin note is not story text, so it never becomes a step. A "?" with
// nothing after it is still the margin — an empty question, not a line of
// narration the reader would be shown. Only a leading "?" is the margin; a
// question mark inside a sentence is prose and is left alone.
function isNote(line: string): boolean {
  return !isEscapedLine(line) && /^\s*\?/.test(line);
}

function isConstruct(line: string): boolean {
  return isScene(line) || isChoice(line) || isDirective(line) || isNote(line);
}

// A line that goes on with the text above it: not blank and not a construct. A lone `\` is not
// blank, so it continues the paragraph as an empty line (B.1).
function continuesText(line: string): boolean {
  return line.trim() !== '' && !isConstruct(line);
}

// Round 1 (B.3, C.2): `NAME (mood)` with an optional trailing tag. The tag words are
// case-insensitive and must be the last token of the line; an escaped line is never a name.
function characterLine(raw: string): Person | null {
  if (isEscapedLine(raw)) return null;
  const tag = raw.match(CHARACTER_TAG);
  const head = tag && tag.index !== undefined ? raw.slice(0, tag.index) : raw;
  const match = head.match(NAME_PATTERN);
  if (!match) return null;
  return {
    name: match[1],
    expression: match[2] as string | undefined,
    mode: tag ? (tag[1].toLocaleLowerCase() as CharacterMode) : undefined,
  };
}

// The words the format reserves for a ">" line. A ">" line that opens with one
// of them but does not parse is malformed reserved syntax: it is reported as a
// compiler diagnostic and dropped, never emitted as a story step. A ">" line
// that opens with anything else is the writer's own prose and is preserved.
// Round 1 (C, E.4, E.5) adds check, results, player select, achieve, video, roll and view.
const RESERVED_DIRECTIVE = /^\s*>\s*(?:if|else|end|go\s+to|remember|forget|hub|check|results|player\s+select|achieve|video|roll|view)\b/i;

// The same words, as the arm loop of an `if` sees them at the start of a body.
const RESERVED_BODY = /^(?:if|remember|forget|end|go to|hub|check|results|player select|achieve|video|roll|view)\b/i;

function splitCheck(body: string, variables: NameLookup | undefined): CheckSplit {
  const tokens = body.trim().split(/\s+/);
  let at = -1;
  let width = 0;
  let operator: EngineCheckOperator | null = null;
  for (let index = 1; index < tokens.length && at < 0; index += 1) {
    const word = tokens[index].toLocaleLowerCase();
    const phrase = index + 1 < tokens.length ? `${word} ${tokens[index + 1].toLocaleLowerCase()}` : '';
    if (phrase && PHRASE_OPERATORS[phrase]) {
      at = index;
      width = 2;
      operator = PHRASE_OPERATORS[phrase];
    } else if (OPERATORS[word]) {
      at = index;
      width = 1;
      operator = OPERATORS[word];
    }
  }
  if (at >= 1) {
    return {
      variable: tokens.slice(0, at).join(' '),
      operator,
      value: parseValue(tokens.slice(at + width).join(' ')),
      bare: false,
      unknownOperator: null,
    };
  }
  // Round 1 (E.2): three or more tokens, a typed value at the end, and no variable by the whole
  // name — the token before the value is an operator the engine does not have.
  const last = tokens.length - 1;
  if (tokens.length >= 3 && isTypedValue(tokens[last]) && !resolveToken(body.trim(), variables, 'V').resolved) {
    return {
      variable: tokens.slice(0, last - 1).join(' '),
      operator: null,
      value: parseValue(tokens[last]),
      bare: false,
      unknownOperator: tokens[last - 1],
    };
  }
  return {
    variable: body.trim(), operator: 'equal', value: TRUE_VALUE, bare: true, unknownOperator: null,
  };
}

/** Round 1 (C, type 15): the payload of a `> roll:` line, or null when it cannot be read. */
function parseRoll(operator: string, spec: string): ScriptRoll | null {
  const range = spec.match(/^(-?\d+(?:\.\d+)?)\.\.(-?\d+(?:\.\d+)?)(?:\s+([+-]\d+(?:\.\d+)?))?$/);
  if (range) {
    let type: ScriptRoll['type'] = 'set';
    if (operator === '+=') type = 'increase';
    if (operator === '-=') type = 'decrease';
    const roll: ScriptRoll = { type, min: Number(range[1]), max: Number(range[2]) };
    if (range[3] !== undefined) roll.modifier = Number(range[3]);
    return roll;
  }
  if (operator === '=' && /^\d*d\d+([+-]\d+)?$/i.test(spec)) return { type: 'dice', diceNotation: spec };
  return null;
}

export function parse(script: string, options: ParseOptions = {}): ScriptAst {
  if (typeof script !== 'string') throw new TypeError('parse requires script text');
  const names = options.names ?? {};
  // Line endings and the byte-order mark are transport, not text. An editor that
  // saves UTF-8 with a BOM puts an invisible character in front of the first
  // line, and without this the episode's own first heading stops being a heading.
  const source = script.startsWith(BYTE_ORDER_MARK) ? script.slice(BYTE_ORDER_MARK.length) : script;
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const ast: ScriptAst = {
    type: 'script', title: null, scenes: [], markers: [],
  };
  let scene: Scene | null = null;

  const marker = (item: Marker): void => {
    ast.markers.push(item);
  };
  // Scene names are how "→" and "go to" find their target, so two scenes wearing
  // the same name is an ambiguity the writer has to settle. The first line a
  // name was seen on travels with the diagnostic so both lines can be named.
  const sceneLines = new Map<string, number>();
  // An answer is the words on the button. A "*" line with no text after it would
  // reach the engine as a blank button the reader cannot read, so it is a
  // diagnostic and is not appended. Answers that carry text are unaffected.
  // Round 1 (G.2): the count of dropped lines travels on the block as `droppedAnswers`.
  const readAnswers = (from: number): { answers: ChoiceAnswer[]; dropped: number; cursor: number } => {
    const answers: ChoiceAnswer[] = [];
    let dropped = 0;
    let cursor = from;
    while (cursor < lines.length && isChoice(lines[cursor])) {
      const item = parseRoutedItem(lines[cursor].replace(/^\s*\*\s?/, ''));
      if (String(item.text ?? '').trim()) answers.push({ ...item, line: cursor + 1 });
      else {
        marker({ line: cursor + 1, value: lines[cursor].trim(), reason: 'empty choice answer' });
        dropped += 1;
      }
      cursor += 1;
    }
    return { answers, dropped, cursor };
  };
  const ensureScene = (line: number): Scene => {
    if (!scene) {
      scene = {
        type: 'scene',
        name: 'Before the first scene',
        location: null,
        locationId: null,
        line,
        blocks: [],
        ending: null,
      };
      ast.scenes.push(scene);
      marker({ line, value: 'UNTITLED', reason: 'content before first scene heading' });
    }
    return scene;
  };
  // The text lines that go on from `from`: until a blank line or a construct, each unescaped.
  const attachedText = (from: number): { text: string[]; cursor: number } => {
    const text: string[] = [];
    let cursor = from;
    while (cursor < lines.length && continuesText(lines[cursor])) {
      text.push(textLine(lines[cursor]));
      cursor += 1;
    }
    return { text, cursor };
  };
  // Round 1 (D.1): a choice block with its speaker, from the NAME line and the prompt. The four
  // speaker keys are present only when a NAME line was written (a choice without one keeps the
  // frozen keys), and `droppedAnswers` only when a `*` line was dropped (G.2).
  const choiceBlock = (
    person: Person | null,
    personLine: number,
    cast: { character: Resolved; expression: Resolved } | null,
    text: string,
    answers: ChoiceAnswer[],
    dropped: number,
  ): ChoiceBlock => {
    const block: ChoiceBlock = person && cast
      ? {
        type: 'choice',
        name: person.name,
        characterId: cast.character.id,
        expression: person.expression ?? null,
        characterExpressionId: cast.expression.id,
        text,
        answers,
        line: personLine,
      }
      : {
        type: 'choice', text, answers, line: personLine,
      };
    if (dropped > 0) {
      const { line: blockLine, ...head } = block;
      return { ...head, droppedAnswers: dropped, line: blockLine } as ChoiceBlock;
    }
    return block;
  };
  const resolveCast = (person: Person): { character: Resolved; expression: Resolved } => ({
    character: resolveToken(person.name, names.characters, 'C'),
    expression: person.expression
      ? resolveToken(person.expression, names.expressions, 'E')
      : { id: null, resolved: true },
  });
  const castMarkers = (person: Person, cast: { character: Resolved; expression: Resolved }, line: number): void => {
    if (!cast.character.resolved) marker({ line, value: person.name, reason: 'unknown character' });
    if (person.expression && !cast.expression.resolved) marker({ line, value: person.expression, reason: 'unknown expression' });
  };

  let index = 0;
  while (index < lines.length && lines[index].trim() === '') index += 1;
  if (lines[index]?.startsWith('# ')) {
    ast.title = lines[index].slice(2).trim();
    index += 1;
  }

  // Reads the line at `at` and whatever lines belong with it (a hub's cards, an
  // if's arms, a run of answers, a paragraph), and returns the index of the next
  // unread line. The frozen loop said `continue` where this returns.
  const readLine = (at: number): number => {
    const raw = lines[at];
    const trimmed = raw.trim();
    const line = at + 1;
    if (!trimmed) return at + 1;

    if (isScene(raw)) {
      const heading = parseHeading(raw);
      const location = resolveToken(heading.location, names.locations, 'L');
      scene = {
        type: 'scene',
        name: heading.name,
        location: heading.location,
        locationId: location.id,
        line,
        blocks: [],
        ending: null,
      };
      if (heading.location && !location.resolved) marker({ line, value: heading.location, reason: 'unknown location' });
      const key = heading.name.toLocaleLowerCase();
      if (heading.name) {
        const first = sceneLines.get(key);
        if (first === undefined) sceneLines.set(key, line);
        else {
          marker({
            line, value: heading.name, reason: 'duplicate scene heading', firstLine: first,
          });
        }
      }
      ast.scenes.push(scene);
      return at + 1;
    }

    const current = ensureScene(line);
    // Round 1 (B.1): the header ends at the first UNESCAPED `|`; the frozen regex's trims are kept
    // (the header trimmed, the subheader with its leading whitespace removed).
    const hubHead = isEscapedLine(raw) ? null : raw.match(/^\s*>\s*hub:\s*(.*)$/);
    const hubSplit = hubHead ? maskEscapes(hubHead[1]).indexOf('|') : -1;
    if (hubHead && hubSplit >= 0) {
      const body = hubHead[1];
      const block: HubBlock = {
        type: 'hub',
        header: unescapeLine(body.slice(0, hubSplit).trim()),
        subheader: unescapeLine(body.slice(hubSplit + 1).replace(/^\s+/, '')),
        options: [],
        line,
      };
      let cursor = at + 1;
      while (cursor < lines.length && isHubOption(lines[cursor])) {
        block.options.push({ ...parseRoutedItem(lines[cursor].replace(/^\s*-\s?/, '')), line: cursor + 1 });
        cursor += 1;
      }
      current.blocks.push(block);
      return cursor;
    }

    const check = isDirective(raw) ? raw.match(/^\s*>\s*if:\s*(.+?)\s*$/i) : null;
    if (check) {
      const parsed = splitCheck(check[1], names.variables);
      const variable = resolveToken(parsed.variable, names.variables, 'V');
      const block: IfDirective = {
        type: 'directive',
        directive: 'if',
        variable: parsed.variable,
        variableId: variable.id,
        operator: parsed.operator,
        value: parsed.value,
        arms: [],
        line,
      };
      if (!variable.resolved) marker({ line, value: parsed.variable, reason: 'unknown variable' });
      // Round 1 (E.2, J): the writer's operator is named and never enters the variable.
      if (parsed.unknownOperator !== null) marker({ line, value: parsed.unknownOperator, reason: 'unknown check operator' });
      let cursor = at + 1;
      // Two spellings of the arms. The machine one names each value and its
      // target ("> true → SCENE"); the human one writes the arm as the lines
      // that follow, routed by "> go to:" or "→", until "> else" or the end of
      // the directive block. An arm with no route falls through to the next scene.
      let style: ArmStyle = null;
      const armHere = (): CheckArm => {
        if (!block.arms.length) block.arms.push({ value: true, target: null, line });
        return block.arms[block.arms.length - 1];
      };
      while (cursor < lines.length && isDirective(lines[cursor])) {
        const body = lines[cursor].replace(/^\s*>\s*/, '').replace(/\s+$/, '');
        const otherwise = body.match(/^else(?:\s*(?:→|->)\s*(.+))?$/i);
        const armRoute = otherwise ? null : body.match(/^(?:go to:\s*|(?:→|->)\s*)(.+)$/i);
        if (otherwise) {
          armHere();
          block.arms.push({ value: false, target: otherwise[1] === undefined ? null : unescapeLine(otherwise[1].trim()), line: cursor + 1 });
          style = 'human';
        } else if (armRoute) {
          const arm = armHere();
          if (arm.target == null) arm.target = unescapeLine(armRoute[1].trim());
          style = 'human';
        } else {
          // Round 1 (B.1): the machine arm splits at the first UNESCAPED arrow; (G.2) a bare typed
          // value with no arrow is an arm with no route — the unrouted arm of the machine spelling.
          const arrow = new RegExp(ARROW_PATTERN.source).exec(maskEscapes(body));
          if (arrow) {
            const valuePart = body.slice(0, arrow.index);
            const targetPart = body.slice(arrow.index + arrow[0].length).trim();
            if (!targetPart || RESERVED_BODY.test(valuePart)) break;
            block.arms.push({ value: parseValue(unescapeLine(valuePart)), target: unescapeLine(targetPart), line: cursor + 1 });
          } else {
            if (RESERVED_BODY.test(body) || !isTypedValue(unescapeLine(body))) break;
            block.arms.push({ value: parseValue(unescapeLine(body)), target: null, line: cursor + 1 });
          }
          style = 'machine';
        }
        cursor += 1;
      }
      // A human-spelled `if` with no `else` still has a false path: it falls
      // through to the next scene.
      if (style === 'human' && block.arms.length === 1) {
        block.arms.push({ value: false, target: null, line });
      }
      current.blocks.push(block);
      return cursor;
    }

    const ending = isDirective(raw) ? raw.match(/^\s*>\s*end(?::\s*(.*?))?\s*$/i) : null;
    if (ending) {
      // Round 1 (C): the text after the colon is the ending's text (a type-5 step); lines that go
      // on directly under the directive are the rest of that text. The name is the first line, or
      // the scene's name when nothing was written.
      // A label of `\` is an empty first line of a multi-line ending text (B.1), which is not the
      // same as a bare `> end` with nothing after the colon.
      const label = ending[1] !== undefined && ending[1].trim() !== '' ? textLine(ending[1].trim()) : null;
      const rest = attachedText(at + 1);
      const text = (label === null ? rest.text : [label, ...rest.text]).join('\n');
      const name = (label ?? '') || current.name;
      // `text` is carried only when something was written: a bare `> end` keeps the frozen block.
      current.blocks.push(text === ''
        ? {
          type: 'directive', directive: 'end', name, line,
        }
        : {
          type: 'directive', directive: 'end', name, text, line,
        });
      // The directive is explicit, so it wins over a margin note.
      current.ending = { name, line, source: 'directive' };
      return rest.cursor;
    }

    const remember = isDirective(raw) ? raw.match(/^\s*>\s*(remember|forget):\s*(.*?)\s*$/i) : null;
    if (remember) {
      const directive = remember[1].toLocaleLowerCase() as 'remember' | 'forget';
      const body = remember[2];
      // Round 1 (E.5): `name += v` / `name -= v` (or `name + v` / `name - v` with a typed value)
      // carry a verb; `name = v` and the bare `name` are the frozen shapes. The frozen rule that the
      // variable holds no `=` stays, so `> remember: =5` is still malformed.
      const compound = body.match(/^([^=]+?)\s*([+-])=\s*(.*?)\s*$/);
      const assign = compound ? null : body.match(/^([^=]+?)\s*=\s*(.*?)\s*$/);
      const spaced = compound || assign || body.includes('=') ? null : body.match(/^(.+?)\s+([+-])\s+(\S+)$/);
      const bare = !compound && !assign && !(spaced && isTypedValue(spaced[3])) && /^[^=]+$/.test(body) ? body : null;
      let variableName: string | null = null;
      let verb: RememberVerb = 'set';
      let assigned: string | undefined;
      if (compound) {
        [, variableName] = compound;
        verb = compound[2] === '+' ? 'increase' : 'decrease';
        [, , , assigned] = compound;
      } else if (assign) {
        [, variableName, assigned] = assign;
      } else if (spaced && isTypedValue(spaced[3])) {
        [, variableName] = spaced;
        verb = spaced[2] === '+' ? 'increase' : 'decrease';
        [, , , assigned] = spaced;
      } else if (bare !== null) {
        variableName = bare;
      }
      if (variableName !== null) {
        const variable = resolveToken(variableName, names.variables, 'V');
        // `> remember: name` sets it true (1); `> remember: name = value` sets the
        // value; `> forget: name` sets it false (0).
        let value: ScriptValue = TRUE_VALUE;
        if (directive === 'forget') value = FALSE_VALUE;
        else if (assigned !== undefined) value = parseValue(assigned);
        // Round 1 fix 2 (BAR-R1-06): the line spelled no value of its own, so the 1 or 0 above is the
        // parser's and not the writer's. `verb` cannot record this — `> remember: v` and
        // `> remember: v = 1` both read as `set` with the value 1 — so the spelling is carried on the
        // block itself, and the compiler keys its 1/0 string leniency on it. Nothing else moves.
        const spelling: { bare?: true } = assigned === undefined ? { bare: true } : {};
        const block: SetDirective = {
          type: 'directive',
          directive,
          variable: variableName,
          variableId: variable.id,
          verb: directive === 'forget' ? 'set' : verb,
          ...spelling,
          value,
          line,
        };
        current.blocks.push(block);
        if (!variable.resolved) marker({ line, value: variableName, reason: 'unknown variable' });
        return at + 1;
      }
    }

    // Round 1 (E.5): `> view: name in place, place`.
    const view = isDirective(raw) ? raw.match(/^\s*>\s*view:\s*(.+)\s+in\s+(.+?)\s*$/i) : null;
    if (view) {
      const variable = resolveToken(view[1], names.variables, 'V');
      const places = view[2].split(',').map((place) => place.trim()).filter((place) => place !== '');
      const block: SetDirective = {
        type: 'directive',
        directive: 'view',
        variable: view[1],
        variableId: variable.id,
        verb: 'view',
        value: places,
        line,
      };
      current.blocks.push(block);
      if (!variable.resolved) marker({ line, value: view[1], reason: 'unknown variable' });
      places.forEach((place) => {
        if (!VIEW_PLACES.has(place.toLocaleLowerCase())) marker({ line, value: place, reason: 'malformed value' });
      });
      return at + 1;
    }

    const goTo = isDirective(raw) ? raw.match(/^\s*>\s*go to:\s*(.+?)\s*$/i) : null;
    if (goTo) {
      current.blocks.push({
        type: 'directive', directive: 'goto', target: unescapeLine(goTo[1]), line,
      });
      return at + 1;
    }

    // Round 1 (E.4): the check envelope — `> check: <json>` with `check` (an object) and `switch`
    // (an array), verbatim from the read. Anything else on the line is malformed.
    const envelope = isDirective(raw) ? raw.match(/^\s*>\s*check:\s*(.*?)\s*$/i) : null;
    if (envelope) {
      let payload: { check: unknown; switch: unknown[] } | null = null;
      try {
        const parsed: unknown = JSON.parse(envelope[1]);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const record = parsed as { check?: unknown; switch?: unknown };
          if (record.check && typeof record.check === 'object' && !Array.isArray(record.check) && Array.isArray(record.switch)) {
            payload = { check: record.check, switch: record.switch };
          }
        }
      } catch {
        payload = null;
      }
      if (payload) {
        const block: CheckEnvelope = {
          type: 'directive', directive: 'check', payload, line,
        };
        current.blocks.push(block);
        return at + 1;
      }
    }

    // Round 1 (C, type 14): `> video: <label>` and the text lines under it.
    const video = isDirective(raw) ? raw.match(/^\s*>\s*video:\s*(.*?)\s*$/i) : null;
    if (video) {
      const rest = attachedText(at + 1);
      const block: VideoBlock = {
        type: 'video', label: unescapeLine(video[1]), text: rest.text.join('\n'), line,
      };
      current.blocks.push(block);
      return rest.cursor;
    }

    // Round 1 (C, type 13): `> achieve: <achievementDescription>` and the text lines under it.
    const achieve = isDirective(raw) ? raw.match(/^\s*>\s*achieve:\s*(.*?)\s*$/i) : null;
    if (achieve) {
      const rest = attachedText(at + 1);
      const block: AchieveBlock = {
        type: 'achieve', description: unescapeLine(achieve[1]), text: rest.text.join('\n'), line,
      };
      current.blocks.push(block);
      return rest.cursor;
    }

    // Round 1 (C, type 15): `> roll: <variable> = <min>..<max> [<modifier>]`, `+=`, `-=`, or a dice notation.
    const roll = isDirective(raw) ? raw.match(/^\s*>\s*roll:\s*(.+?)\s*(\+=|-=|=)\s*(.+?)\s*$/i) : null;
    const rollPayload = roll ? parseRoll(roll[2], roll[3]) : null;
    if (roll && rollPayload) {
      const variable = resolveToken(roll[1], names.variables, 'V');
      const block: RollBlock = {
        type: 'roll', variable: roll[1], variableId: variable.id, roll: rollPayload, line,
      };
      current.blocks.push(block);
      if (!variable.resolved) marker({ line, value: roll[1], reason: 'unknown variable' });
      return at + 1;
    }

    // Round 1 (C, types 6 and 7): `> results: <subType>` / `> player select` and their text lines.
    const results = isDirective(raw) ? raw.match(/^\s*>\s*results(?::\s*(.*?))?\s*$/i) : null;
    const playerSelect = isDirective(raw) ? raw.match(/^\s*>\s*player\s+select\s*$/i) : null;
    if (results || playerSelect) {
      const subType = results && results[1] ? results[1] : null;
      const rest = attachedText(at + 1);
      const block: LegacyBlock = {
        type: 'legacy', stepTypeId: results ? 6 : 7, subType, text: rest.text.join('\n'), line,
      };
      current.blocks.push(block);
      if (subType !== null && !RESULT_SUB_TYPES.has(subType.toLocaleLowerCase())) marker({ line, value: subType, reason: 'malformed value' });
      return rest.cursor;
    }

    // Every spelling of a directive has now had its turn. In this grammar a line
    // that opens with ">" is always a directive and prose never starts with one,
    // so whatever is left here is either reserved syntax the compile could not
    // read ("> if:" with no check, "> go to:" with no target, an orphan
    // "> else") or a directive the format does not define ("> foo: bar"). Both
    // are diagnostics and neither leaves a step: emitting one as narration would
    // show the reader the writer's stage direction.
    if (isDirective(raw)) {
      const keyword = trimmed.replace(/^>\s*/, '').match(/^[^\s:]+/)?.[0] ?? '';
      if (!keyword || RESERVED_DIRECTIVE.test(raw)) marker({ line, value: trimmed, reason: 'malformed directive' });
      else {
        marker({
          line, value: keyword, reason: 'unknown directive', text: trimmed,
        });
      }
      return at + 1;
    }

    if (isNote(raw)) {
      const note = trimmed.replace(/^\?\s*/, '');
      current.blocks.push({ type: 'note', text: note, line });
      marker({ line, value: note, reason: 'note' });
      // The adapter's spelling of an ending, written before `> end` existed:
      // a margin note that opens with the word "ending".
      if (/^ending\b/i.test(note) && current.ending == null) {
        current.ending = { name: current.name, line, source: 'note' };
      }
      return at + 1;
    }

    if (isChoice(raw)) {
      const read = readAnswers(at);
      // A run of "*" lines that were all empty is nothing but diagnostics; a
      // choice step with no answers would be a dead button on the screen.
      if (read.answers.length) current.blocks.push(choiceBlock(null, line, null, '', read.answers, read.dropped));
      return read.cursor;
    }

    const person = characterLine(raw);
    // Round 1 (C, type 10): a reaction line stands alone — it has no text line under it.
    if (person && person.mode === 'reaction') {
      const cast = resolveCast(person);
      const block: CharacterBlock = {
        type: 'character',
        name: person.name,
        characterId: cast.character.id,
        expression: person.expression ?? null,
        characterExpressionId: cast.expression.id,
        mode: 'reaction',
        text: '',
        line,
      };
      current.blocks.push(block);
      castMarkers(person, cast, line);
      return at + 1;
    }
    if (person && at + 1 < lines.length && continuesText(lines[at + 1])) {
      const cast = resolveCast(person);
      const run = attachedText(at + 1);
      // Round 1 (D.1): a NAME line whose run is followed immediately by a `*` run is the choice's
      // speaker and prompt — one choice block, never a dialogue block plus a promptless choice.
      if (!person.mode && run.cursor < lines.length && isChoice(lines[run.cursor])) {
        castMarkers(person, cast, line);
        const read = readAnswers(run.cursor);
        if (read.answers.length) current.blocks.push(choiceBlock(person, line, cast, run.text.join('\n'), read.answers, read.dropped));
        else {
          // With every answer diagnosed away there is no choice left, so the lines under the NAME
          // line are the dialogue they would have been without the "*" run.
          current.blocks.push({
            type: 'character',
            name: person.name,
            characterId: cast.character.id,
            expression: person.expression ?? null,
            characterExpressionId: cast.expression.id,
            text: run.text.join('\n'),
            line,
          });
        }
        return read.cursor;
      }
      // Round 1 (C): `mode` sits before `text` when the line carried a tag; a type-1 block keeps the
      // frozen keys in the frozen order.
      const head = {
        type: 'character' as const,
        name: person.name,
        characterId: cast.character.id,
        expression: person.expression ?? null,
        characterExpressionId: cast.expression.id,
      };
      const block: CharacterBlock = person.mode
        ? {
          ...head, mode: person.mode, text: run.text.join('\n'), line,
        }
        : { ...head, text: run.text.join('\n'), line };
      current.blocks.push(block);
      castMarkers(person, cast, line);
      return run.cursor;
    }
    // Round 1 (D.1): a NAME line directly followed by `*` lines is a choice with a speaker and no prompt.
    if (person && !person.mode && at + 1 < lines.length && isChoice(lines[at + 1])) {
      const cast = resolveCast(person);
      castMarkers(person, cast, line);
      const read = readAnswers(at + 1);
      if (read.answers.length) current.blocks.push(choiceBlock(person, line, cast, '', read.answers, read.dropped));
      return read.cursor;
    }

    const paragraph: string[] = [];
    let cursor = at;
    while (cursor < lines.length && continuesText(lines[cursor])) {
      paragraph.push(textLine(lines[cursor]));
      cursor += 1;
    }
    if (!paragraph.length) {
      current.blocks.push({ type: 'narration', text: textLine(raw), line });
      return at + 1;
    }
    if (cursor < lines.length && isChoice(lines[cursor])) {
      const read = readAnswers(cursor);
      // With every answer diagnosed away there is no choice left, so the lines
      // above it are the narration they were before the "*" run.
      if (read.answers.length) current.blocks.push(choiceBlock(null, line, null, paragraph.join('\n'), read.answers, read.dropped));
      else current.blocks.push({ type: 'narration', text: paragraph.join('\n'), line });
      return read.cursor;
    }
    current.blocks.push({ type: 'narration', text: paragraph.join('\n'), line });
    return cursor;
  };

  while (index < lines.length) index = readLine(index);

  const scenesByName = new Map<string, Scene>(
    ast.scenes.map((item): [string, Scene] => [item.name.toLocaleLowerCase(), item]),
  );
  const route = (items: Routable[]): void => {
    for (let at = 0; at < items.length; at += 1) {
      const item = items[at];
      if (item.target) {
        const target = scenesByName.get(item.target.toLocaleLowerCase());
        if (target) item.targetScene = target.name;
        else marker({ line: item.line, value: item.target, reason: 'unknown target' });
      }
    }
  };
  const routeBlock = (block: Block): void => {
    if (block.type === 'directive' && block.directive === 'goto') route([block]);
    if (block.type === 'choice') route(block.answers);
    if (block.type === 'directive' && block.directive === 'if') route(block.arms);
    if (block.type === 'hub') route(block.options);
  };
  ast.scenes.forEach((itemScene) => {
    itemScene.blocks.forEach(routeBlock);
  });
  return ast;
}
